/* global Vue, VueRouter, CONTEXT, AZURE_URI, LOCALE, PREFIX */

import Test from './views/Test'
import Dashboard from './views/Dashboard'
import Awards from './views/Awards'
import ObtainPoints from './views/ObtainPoints'
import TransferPoints from './views/TransferPoints'
import Activity from './views/Activity'
import MainTitle from './components/MainTitle'
import { Notifications, useState, setSuccessAndStopLoading, setErrorAndStopLoading, startLoading, stopLoading } from '../apps/components/Notifications'
import ReservationsPage from './components/ReservationsPage'
import Error403 from './components/Error403'
import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import '../validation-rules'
import AuthService from '../AuthService'
import { isDebugEnabled } from '../utils-common'

const i18n = CreateI18n(messagesSpecific)

const appElement = document.getElementById('loyalty-app')
const isMaiFamilyVariant = appElement?.dataset.variant === 'maifamily'
const maiFamily = isMaiFamilyVariant
  ? { aboutUrl: appElement.dataset.aboutUrl, aboutText: appElement.dataset.aboutText, propertiesUrl: appElement.dataset.propertiesUrl }
  : null

const PENDING_NOTIFICATIONS_KEY = 'loyaltyPendingNotifications'

// MaiFamily members have their own profile page (/maifamily/), MaiStar members stay on /loyalty/profile/
function redirectToProfileVariant (isMaiFamily) {
  if (!!isMaiFamily === isMaiFamilyVariant) {
    return false
  }
  // keep success notifications (e.g. profile saved) so they are shown again after the redirect
  const { state } = useState()
  const pending = state.notifications.value.filter(n => n.type === 'success').map(n => n.details)
  if (pending.length) {
    try {
      window.sessionStorage.setItem(PENDING_NOTIFICATIONS_KEY, JSON.stringify(pending))
    } catch (e) {}
  }
  window.location.replace(PREFIX + (isMaiFamily ? '/maifamily/' : '/loyalty/profile/') + window.location.hash)
  return true
}

function showPendingNotifications () {
  let pending = []
  try {
    pending = JSON.parse(window.sessionStorage.getItem(PENDING_NOTIFICATIONS_KEY)) || []
    window.sessionStorage.removeItem(PENDING_NOTIFICATIONS_KEY)
  } catch (e) {}
  pending.forEach(details => setSuccessAndStopLoading(details))
}

const router = VueRouter.createRouter({
  history: VueRouter.createWebHashHistory(),
  routes: [
    { path: '/', name: 'Dashboard', component: Dashboard, meta: { title: 'dashboard.title' } },
    {
      path: '/test',
      name: 'Test',
      component: Test,
      meta: { title: 'Test' },
      beforeEnter: () => { return isDebugEnabled.value || (CONTEXT !== 'production') },
    },
    { path: '/awards', name: 'AllAwards', component: Awards, meta: { title: 'awards.title' } },
    { path: '/awards/recommended', name: 'RecommendedAwards', component: Awards, meta: { title: 'awards.title' } },
    { path: '/reservations', name: 'ReservationsActive', component: ReservationsPage, props: { active: true, limit: 50 }, meta: { title: 'reservations.title' } },
    { path: '/reservations/past', name: 'ReservationsPast', component: ReservationsPage, props: { active: false, limit: 50 }, meta: { title: 'reservations.title' } },
    { path: '/activity', name: 'PointsActivity', component: Activity, meta: { title: 'activity.title' }, beforeEnter: () => { return AuthService.isMaiFamily ? { name: 'Dashboard' } : true } },
    { path: '/points/obtain', name: 'ObtainPoints', component: ObtainPoints, meta: { title: 'obtainPoints.mainTitle' } },
    { path: '/points/transfer', name: 'TransferPoints', component: TransferPoints, meta: { title: 'transferPoints.mainTitle' } },
    { path: '/:pathMatch(.*)', component: { template: '<h1 style="color: red">Page Not Found</h1>' } },
  ],
  scrollBehavior (to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    } else if (savedPosition) {
      return savedPosition
    } else {
      return { left: 0, top: 0 }
    }
  },
})

const LoyaltyApp = Vue.createApp({
  name: 'LoyaltyApp',
  components: { ReservationsPage, MainTitle, Error403, Notifications },
  template: `
  <Notifications />
  <div v-show="$route.name !== 'Dashboard'" class="header-hybrid-padding">
    <MainTitle
      :title="$t($route.meta.title ?? 'loading')"
      :backText="($route.name === 'ObtainPoints' || $route.name === 'TransferPoints') ?  $t('activity.title') : $t('myProfile.title')"
      :backRoute="($route.name === 'ObtainPoints' || $route.name === 'TransferPoints') ? { name: 'PointsActivity' } : { name: 'Dashboard' }"
      class="main-title--bg no-margin"
    />
  </div>

  <div v-show="$route.path.startsWith('/awards') && loyaltyId" class="tabs tabs--loyalty container">
    <div class="tabs__thumbs swiper">
      <div class="swiper-wrapper">
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'AllAwards' }"
          @click="router.push({ name: 'AllAwards' })">
          <span>{{ $t('awards.all') }}</span>
        </div>
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'RecommendedAwards' }"
          @click="router.push({ name: 'RecommendedAwards' })">
          <span>{{ $t('awards.recommended') }}</span>
        </div>
      </div>
    </div>
  </div>

  <div v-show="$route.path.startsWith('/reservations') && loyaltyId" class="tabs tabs--loyalty container">
    <div class="tabs__thumbs swiper">
      <div class="swiper-wrapper">
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'ReservationsActive' }"
          @click="router.push({ name: 'ReservationsActive' })">
          <span>{{ $t('reservations.activeReservations') }}</span>
        </div>
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'ReservationsPast' }"
          @click="router.push({ name: 'ReservationsPast' })">
          <span>{{ $t('reservations.pastReservations') }}</span>
        </div>
      </div>
    </div>
  </div>

  <router-view v-if="loyaltyId" :loyaltyId="loyaltyId" :properties="properties" />

  <Error403 v-show="authIsFinished && !state.isLoading.value && loyaltyId < 1" />
`,
  setup () {
    const { state } = useState()
    const loyaltyId = Vue.ref(0)
    const authIsFinished = Vue.ref(false)

    const header = document.querySelector('.header')
    const logo = document.querySelector('.header-logos')
    const logoImg = document.querySelector('.header-logos__maistra img')
    const originalLogo = logoImg.src
    const stickyLogo = document.querySelector('.header-logos__maistra').dataset.sticky
    const showOnDashEls = document.querySelectorAll('.js-show-on-dash')
    const rewardsEL = document.querySelector('.js-rewards')
    const properties = Vue.reactive([])

    function makeHeaderDark () {
      header.classList.add('header--dark')
      logo.classList.remove('header-logos--invert')
      logoImg.src = originalLogo
      window.PREVENT_LOGO_CHANGE = false
      showOnDashEls.forEach(el => {
        el.classList.remove('is-hidden')
      })
    }

    function makeHeaderLight () {
      header.classList.remove('header--dark')
      logo.classList.add('header-logos--invert')
      logoImg.src = stickyLogo
      window.PREVENT_LOGO_CHANGE = true
      showOnDashEls.forEach(el => {
        el.classList.add('is-hidden')
      })
    }

    const loyaltyIdLoaded = new Event('loyaltyIdLoaded')

    router.afterEach((to, from) => {
      if (to.path === '/') {
        if (loyaltyId.value > 0) {
          makeHeaderDark()
          document.dispatchEvent(loyaltyIdLoaded)
        } else {
          makeHeaderLight()
        }

        Vue.watch(loyaltyId, (id) => {
          if (id > 0) {
            makeHeaderDark()
            document.dispatchEvent(loyaltyIdLoaded)
          } else {
            makeHeaderLight()
          }
        })
      } else {
        makeHeaderLight()
      }

      if ((to.path === '/activity' || to.path.includes('/reservations')) && rewardsEL) {
        rewardsEL.classList.remove('is-hidden')
      }
    })

    async function getProperties () {
      try {
        Object.assign(properties, null)
        const response = await fetch(`${AZURE_URI}GetProperties?locale=${LOCALE}`)
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        Object.assign(properties, await response.json())
        properties.sort((a, b) => (a.propertyListingWeight ?? Number.MAX_VALUE) - (b.propertyListingWeight ?? Number.MAX_VALUE))
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    }

    Vue.onMounted(() => {
      getProperties()
      startLoading('loyalty-app')
      AuthService.on('ended', () => {
        if (AuthService.accountId === undefined) {
          stopLoading('loyalty-app')
          authIsFinished.value = true
        }
      })
      authIsFinished.value = AuthService.isFinished
      if (authIsFinished.value) {
        stopLoading('loyalty-app')
      }
      AuthService.on('authenticated', e => {
        if (redirectToProfileVariant(e?.detail.isMaiFamily)) {
          return
        }
        stopLoading('loyalty-app')
        showPendingNotifications()
        try {
          if (Number.isInteger(e?.detail.loyaltyId)) {
            loyaltyId.value = e.detail.loyaltyId
          } else {
            makeHeaderLight()
            throw Error('no loyaltyID')
          }
        } catch (e) {
          makeHeaderLight()
          setErrorAndStopLoading(e, true)
        }
        authIsFinished.value = true

        if (e?.detail.isMaiFamily && router.currentRoute.value.name === 'PointsActivity') {
          router.replace({ name: 'Dashboard' })
        }
      })
    })

    return { state, setSuccessAndStopLoading, setErrorAndStopLoading, router, loyaltyId, authIsFinished, properties }
  },
})

if (appElement) {
  LoyaltyApp.config.globalProperties.$AuthService = AuthService // same service is importent on all apps
  LoyaltyApp.provide('maiFamily', maiFamily)
  LoyaltyApp.use(i18n)
  LoyaltyApp.use(router)
  LoyaltyApp.mount(appElement)
}

export default LoyaltyApp
